import React, { ReactElement, useState } from 'react';
import { isEmpty } from 'lodash';

import Carousel, { Slide } from '../../library/carousel/carousel';
import Image from '../../library/image/image';
import Modal from '../modal/modal';

import { useMediaQuery } from '../../shared/hooks/hooks';

import { isArrayWithLength } from '../../shared/utils/validators/validators';

import './product-image-gallery.scss';

interface ImageInterface {
  altText: string;
  secondaryAltText?: string;
  secondaryUrl?: string;
  url: string;
}

export interface GalleryImages {
  product?: ImageInterface[];
  thumbnail?: ImageInterface[];
  zoom?: ImageInterface[];
}

interface ProductImageGalleryProps {
  fallbackProductImage: string;
  galleryImages: GalleryImages;
  height?: string;
  showFallbackProductImage?: boolean;
  width?: string;
}

function ProductImageGallery(props: ProductImageGalleryProps) {
  const {
    fallbackProductImage,
    galleryImages,
    height = '300',
    showFallbackProductImage,
    width = '300'
  } = props;

  const [imageIdx, setImageIdx] = useState(0);
  const [modalImage, setModalImage] = useState<ImageInterface | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const breakpoint = useMediaQuery();

  let zoomImage: ImageInterface | null = null;

  function openImageModal(zoomImage) {
    if (zoomImage) {
      setModalImage(zoomImage);
      setIsImageModalOpen(true);
    }
  }

  function closeImageModal() {
    setIsImageModalOpen(false);
    setModalImage(null);
  }

  if (Array.isArray(galleryImages?.zoom)) {
    zoomImage = galleryImages.zoom[imageIdx];
  }

  return (
    <div
      className="product-image-gallery"
      styleName={
        showFallbackProductImage
          ? 'product-fallback-container'
          : 'product-carousel-container'
      }
    >
      {!isEmpty(galleryImages) && isArrayWithLength(galleryImages?.product) && (
        <div
          className="product-image-gallery__product-carousel"
          styleName="product-carousel"
        >
          <Carousel
            hasFullWidthSlides
            onSlideChange={setImageIdx}
            showIndicators={breakpoint.isXS}
            slideIndex={imageIdx}
          >
            {galleryImages.product?.reduce(
              (acc: ReactElement[], image, idx) => {
                acc.push(
                  <Slide key={`gi-${idx}`} shouldLazyLoad>
                    <button
                      onClick={() => {
                        openImageModal(zoomImage);
                      }}
                      styleName="product-carousel-slide"
                    >
                      <Image
                        alt={image.altText}
                        className="product-image-gallery__product-carousel-image"
                        height={height}
                        src={image.url}
                        title={image.altText}
                        width={width}
                      />
                    </button>
                  </Slide>
                );
                return acc;
              },
              []
            )}
          </Carousel>
        </div>
      )}

      {showFallbackProductImage && (
        <div
          style={{
            backgroundImage: `url(${fallbackProductImage})`,
            height: '190px'
          }}
          styleName="product-fallback-image"
        />
      )}

      {isArrayWithLength(galleryImages?.thumbnail) && breakpoint.isMinSm && (
        <div
          className="product-image-gallery__product-carousel-thumbnails"
          styleName="product-carousel-thumbnails"
        >
          {galleryImages.thumbnail?.reduce((acc: ReactElement[], img, i) => {
            acc.push(
              <button
                className={
                  i === imageIdx
                    ? 'product-image-gallery__product-carousel-thumbnail-selected'
                    : 'product-image-gallery__product-carousel-thumbnail'
                }
                key={`thumbnail-${i}`}
                onClick={() => setImageIdx(i)}
                styleName={
                  i === imageIdx
                    ? 'product-carousel-thumbnail-selected'
                    : 'product-carousel-thumbnail'
                }
              >
                <img
                  alt={img.altText}
                  height="68"
                  src={img.url}
                  title={img.altText}
                  width="68"
                />
              </button>
            );
            return acc;
          }, [])}
        </div>
      )}

      <Modal isOpen={isImageModalOpen} onClose={closeImageModal}>
        <div styleName="modal-images-container">
          {modalImage?.url && (
            <div>
              <img
                alt={modalImage.altText}
                height="500"
                src={modalImage.url}
                width="500"
              />
            </div>
          )}
          {modalImage?.secondaryUrl && (
            <div>
              <img
                alt={modalImage.secondaryAltText}
                height="500"
                src={modalImage.secondaryUrl}
                width="500"
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default ProductImageGallery;
